<template>
  <div class="widget-body" :class="viewMode" v-resize:throttle.100="setWidgetViewMode">
    <c-loader v-if="isLoading" />
    <v-alert show v-else-if="!ric" variant="light" align="center">{{ i18n('messages.noRic') }}</v-alert>
    <v-alert show v-else-if="isError" variant="red" align="center">{{ i18n('error', 'globalMessages') }}</v-alert>
    <v-alert show v-if="isNoData" variant="light" align="center">{{ i18n('nodata', 'globalMessages') }}</v-alert>
    <template v-else>
      <c-streaming-error :show="hasStreamingError" />
      <v-stock-chart class="chart" :options="chartOptions" ref="chart" />
      <div class="base-info" v-if="dataAvailable">
        <div class="symbol-info">
          <div class="instrument-info">
            <v-flag class="mr-2 mb-1" :width="24" :countryCode="data['q._COUNTRY'] || ''">
              <slot>{{ i18n(data['q._COUNTRY'], 'countries') }}</slot>
            </v-flag>
            <span class="ric">{{ data['q._TCR'] }}</span>
            <span class="display-name">{{ data['q._DSPLY_NAME'] }}</span>
            <c-halt-indicator :tradeHaltValue="data['q.TRD_STATUS']" :toolTipRequired="true" />
          </div>
          <c-tick-flash class="price-container" :value="data['q._NETCHNG_1']" :data-abs-pole="absPriceChangePole">
            <div class="price">
              <c-trend-arrow :orientation="trend" />
              <v-fidlet :data="data['q._TRDPRC_1']" :type="formats['q._TRDPRC_1']" />
            </div>
            <div class="price-change">
              <v-fidlet :data="data['q._NETCHNG_1']" :type="formats['q._NETCHNG_1']" />
              <v-fidlet :data="data['q._PCTCHNG']" :type="formats['q._PCTCHNG']" />
            </div>
          </c-tick-flash>
          <div class="action-btn">
            <c-action-buttons-bar
              :buy="buyAction"
              :sell="sellAction"
              :cover="coverAction"
              :short="shortAction"
              :symbol="ric"
              :ticketType="ticketType"
              :wid="wid"
              :embedded="embedded"
            />
          </div>

          <div class="earnings-date-container">
            <span class="highlight">{{ i18n(`labels.nextEarnings`) }}</span>
            <span>
              <span v-if="displayedEarningsDate">
                {{ displayedEarningsDate }}
              </span>
              <span class="market-phase" v-if="data['nextMarketPhase']">
                {{ i18n(`labels.marketPhaseMap.${data['nextMarketPhase']}`) }}
              </span>
            </span>
          </div>
          <div class="d-flex analyst-flex">
            <div class="d-flex">
              <span class="label">{{ i18n('labels.analystAverage') }}</span>
              <span class="value" :class="data['recomText']">{{
                data['recomText'] ? i18n(`labels.${data['recomText']}`) : '-'
              }}</span>
            </div>
            <div class="d-flex">
              <span class="label">{{ i18n('labels.targetPrice') }}</span>
              <span class="value" :class="targetPriceClass" v-if="data['rkd4.TARGETPRICE']">{{
                $f(data['rkd4.TARGETPRICE'], 'float.price')
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="fid-list" v-if="dataAvailable">
        <c-open-order-entry
          action="bid"
          :limit="data['q._BID']"
          :buyAction="buyAction"
          :sellAction="sellAction"
          :ric="ric"
          :ticketType="ticketType"
        >
          <div class="label">{{ i18n('labels.bid') }}</div>
          <div class="d-flex">
            <c-tick-flash :value="data['q._BID']">
              <v-fidlet :data="data['q._BID']" :type="formats['q._BID']" />
            </c-tick-flash>
            <c-tick-flash :value="data['q.BIDSIZE']">
              <span class="pl-1">(<v-fidlet :data="data['q.BIDSIZE']" :type="formats['q.BIDSIZE']" />)</span>
            </c-tick-flash>
          </div>
          <span class="sr-only" v-if="embedded">{{
            $t('white-label.screenReaderLabels.screenReaderOrderEntryLabel')
          }}</span>
        </c-open-order-entry>

        <c-open-order-entry
          action="ask"
          :limit="data['q._ASK']"
          :buyAction="buyAction"
          :sellAction="sellAction"
          :ric="ric"
          :ticketType="ticketType"
        >
          <div class="label">{{ i18n('labels.ask') }}</div>
          <div class="d-flex">
            <c-tick-flash :value="data['q._ASK']">
              <v-fidlet :data="data['q._ASK']" :type="formats['q._ASK']" />
            </c-tick-flash>
            <c-tick-flash :value="data['q.ASKSIZE']">
              <span class="pl-1">(<v-fidlet :data="data['q.ASKSIZE']" :type="formats['q.ASKSIZE']" />)</span>
            </c-tick-flash>
          </div>
          <span class="sr-only" v-if="embedded">{{
            $t('white-label.screenReaderLabels.screenReaderOrderEntryLabel')
          }}</span>
        </c-open-order-entry>

        <div>
          <div class="label">{{ i18n('labels.open-close') }}</div>
          <div class="d-flex">
            <c-tick-flash :value="data['q._OPEN_PRC']">
              <v-fidlet :data="data['q._OPEN_PRC']" :type="formats['q._OPEN_PRC']" />
            </c-tick-flash>
            <span class="pl-1 pr-1">/</span>
            <c-tick-flash :value="data['q._HST_CLOSE']">
              <v-fidlet :data="data['q._HST_CLOSE']" :type="formats['q._HST_CLOSE']" />
            </c-tick-flash>
          </div>
        </div>
        <div>
          <div class="label">{{ i18n('labels.high-low') }}</div>
          <div class="d-flex">
            <c-tick-flash :value="data['q._HIGH_1']">
              <v-fidlet :data="data['q._HIGH_1']" :type="formats['q._HIGH_1']" />
            </c-tick-flash>
            <span class="pl-1 pr-1">/</span>
            <c-tick-flash :value="data['q._LOW_1']">
              <v-fidlet :data="data['q._LOW_1']" :type="formats['q._LOW_1']" />
            </c-tick-flash>
          </div>
        </div>

        <div>
          <div class="label">{{ i18n('labels.52-high-low') }}</div>
          <div class="d-flex">
            <c-tick-flash :value="data['q.YRHIGH']">
              <v-fidlet :data="data['q.YRHIGH']" :type="formats['q.YRHIGH']" />
            </c-tick-flash>
            <span class="pl-1 pr-1">/</span>
            <c-tick-flash :value="data['q.YRLOW']">
              <v-fidlet :data="data['q.YRLOW']" :type="formats['q.YRLOW']" />
            </c-tick-flash>
          </div>
        </div>

        <div>
          <div class="label">{{ i18n('labels.vol') }}</div>
          <v-fidlet :data="data['q._ACVOL_1']" :type="formats['q._ACVOL_1']" />
        </div>

        <div>
          <div class="label">{{ i18n('labels.per-eps') }}</div>
          <div class="d-flex">
            <c-tick-flash :value="data['rkd.PRICE_TO_EPS']">
              <v-fidlet :data="data['rkd.PRICE_TO_EPS']" :type="formats['rkd.PRICE_TO_EPS']" />
            </c-tick-flash>
            <span class="pl-1 pr-1">/</span>
            <c-tick-flash :value="data['rkd.EPS']">
              <v-fidlet :data="data['rkd.EPS']" :type="formats['rkd.EPS']" />
            </c-tick-flash>
          </div>
        </div>

        <div>
          <div class="label">{{ i18n('labels.dps-price') }}</div>
          <div class="d-flex">
            <c-tick-flash :value="data['q.DIVIDEND']">
              <v-fidlet :data="data['q.DIVIDEND']" :type="formats['q.DIVIDEND']" />
            </c-tick-flash>
            <c-tick-flash :value="data['q._YIELD']">
              <span class="pl-1 pr-1">/</span>
              <v-fidlet :data="data['q._YIELD']" :type="formats['q._YIELD']" />
            </c-tick-flash>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import widgetCommons from '@mixins/widgetCommons';

import CLoader from '@custom/CLoader';
import CActionButtonsBar from '@custom/CActionButtonsBar';
import COpenOrderEntry from '@custom/COpenOrderEntry';
import CTickFlash from '@custom/CTickFlash';
import CTrendArrow from '@custom/CTrendArrow';
import VStockChart from '@base/VStockChart';
import resize from 'vue-resize-directive';
import streamableFidsFilter from '@/utils/streamableFids';
import CHaltIndicator from '@custom/CHaltIndicator';
import CStreamingError from '@custom/CStreamingError';
import { API } from '@state/api';
import moment from 'moment-timezone';
import { toTimezoneDate } from '@/utils/date';

export default {
  name: 'w-quote-widget',
  mixins: [widgetCommons],
  components: {
    CLoader,
    CActionButtonsBar,
    COpenOrderEntry,
    CTickFlash,
    CTrendArrow,
    VStockChart,
    CHaltIndicator,
    CStreamingError
  },
  directives: {
    resize
  },
  props: {
    wid: {
      type: String,
      required: true
    },
    ric: {
      type: String,
      required: true
    },
    buyAction: {
      type: Function,
      default: undefined
    },
    sellAction: {
      type: Function,
      default: undefined
    },
    coverAction: {
      type: Function,
      default: undefined
    },
    shortAction: {
      type: Function,
      default: undefined
    },
    embedded: {
      type: Boolean
    }
  },
  data() {
    return {
      chartOptions: {
        lang: {
          accessibility: {
            chartContainerLabel: this.getChartTitle(),
            svgContainerLabel: this.getChartTitle(),
            defaultChartTitle: this.getChartTitle()
          }
        },
        chart: {
          animation: false,
          type: 'area',
          margin: 0,
          reflow: false
        },
        series: [
          {
            name: this.i18n('chart.price'),
            data: undefined,
            softThreshold: false
          }
        ],
        tooltip: {
          hideDelay: 50,
          padding: 3
          // outside: true
        },
        rangeSelector: {
          enabled: false
        },
        navigator: {
          enabled: false
        },
        scrollbar: {
          enabled: false
        },
        xAxis: {
          visible: false,
          accessibility: {
            rangeDescription: ''
          }
        },
        yAxis: {
          endOnTick: false,
          startOnTick: false,
          maxPadding: 0.05,
          minPadding: 0.05,
          visible: false,
          accessibility: {
            rangeDescription: ''
          }
        }
      },
      data: {},
      fids: [
        'q.RIC',
        'q._TCR',
        'q._COUNTRY',
        'q._DSPLY_NAME',
        'q._TRDPRC_1',
        'q._NETCHNG_1',
        'q._PCTCHNG',
        'q._HSTCLSDATE',
        'q._BID',
        'q.BIDSIZE',
        'q._ASK',
        'q.ASKSIZE',
        'q._OPEN_PRC',
        'q._HIGH_1',
        'q._LOW_1',
        'q._ACVOL_1',
        'q._TYPE',
        'q.TRD_STATUS',
        'q._HST_CLOSE',
        'q.YRHIGH',
        'q.YRLOW',
        'q._YIELD',
        'rkd.PRICE_TO_EPS',
        'rkd.EPS',
        'q.DIVIDEND'
      ],
      rkdFids: ['rkd.PRICE_TO_EPS', 'rkd.EPS'],
      formats: {},
      hasStreamingError: false,
      isError: false,
      isLoading: true,
      isNoData: false,
      nextSeriesUpdTime: null,
      subscriptionId: null,
      timeseries: [],
      viewBreakpoint: 800,
      viewMode: '',
      lastTimeSeries: 0,
      nextEarningsDate: ''
    };
  },
  computed: {
    absPriceChangePole() {
      return this.getPole(this.data['q._NETCHNG_1']);
    },
    dataAvailable() {
      return Object.keys(this.data).length > 0;
    },
    trend() {
      const map = {
        '+': 'up',
        '-': 'down',
        '': 'right'
      };
      return map[this.absPriceChangePole];
    },
    activeWorkspaceId() {
      return this.$store.getters['workspaceManager/activeWorkspace'].id;
    },
    activeWorkspaceLayout() {
      return this.$store.state.workspaces[this.activeWorkspaceId].layout.type;
    },
    ticketType() {
      let type = this.data['q._TYPE'];
      return type === 'OPT' ? 'option' : 'stock';
    },
    targetPriceClass() {
      if (this.data['rkd4.TARGETPRICE'] === '-' || this.data['rkd4.TARGETPRICE'] === '') {
        return '';
      }
      return this.data['q._TRDPRC_1'] < this.data['rkd4.TARGETPRICE'] ? 'positive' : 'negative';
    },
    displayedEarningsDate() {
      moment.locale(this.$i18n.locale);
      let earningsDate = this.nextEarningsDate ? moment(this.nextEarningsDate).format('MMMM Do, Y') : '';
      return `${earningsDate} ${
        this.nextEarningsDate
          ? '(' +
            moment(this.nextEarningsDate).diff(this.momentTZ().startOf('day'), 'days') +
            ' ' +
            this.$t(`white-label.globalLabels.days`) +
            ')'
          : '-'
      }`;
    }
  },
  watch: {
    ric: {
      handler() {
        this.fetch();
      }
    },
    fontSize() {
      this.$refs.chart.chart.reflow();
    },
    timeseries: {
      handler(value) {
        if (this.$refs.chart.chart) {
          this.chartOptions.series[0].data = value;
          //nexttick is needed here as the chart is not yet initialized when the data is set
          this.$nextTick(() => {
            const min = parseFloat(this.$refs.chart.chart.yAxis[0].min).toFixed(2);
            const max = parseFloat(this.$refs.chart.chart.yAxis[0].max).toFixed(2);
            this.chartOptions.yAxis.accessibility.rangeDescription = `Range: ${min} to ${max}`;
            this.chartOptions.xAxis.accessibility.rangeDescription =
              'Range:' +
              moment(new Date(this.$refs.chart.chart.xAxis[0].min)).format('dddd MMMM Do YYYY, H:mm') +
              ' to ' +
              moment(new Date(this.$refs.chart.chart.xAxis[0].max)).format('dddd MMMM Do YYYY, H:mm');
          });
        }
      }
    },
    '$store.state.applicationSettings.general.settings.applicationLanguage'() {
      this.chartOptions.lang.accessibility.chartContainerLabel = this.getChartTitle();
      this.chartOptions.lang.accessibility.svgContainerLabel = this.getChartTitle();
      this.chartOptions.lang.accessibility.defaultChartTitle = this.getChartTitle();
    }
  },
  methods: {
    i18n(subkey, ns) {
      return this.$t(`white-label.${ns || 'widgets.quote'}.${subkey}`);
    },
    getPole(val, oldVal) {
      if (typeof oldVal === 'undefined') oldVal = 0;
      val = parseFloat(val);
      oldVal = parseFloat(oldVal);
      return val < oldVal ? '-' : val > oldVal ? '+' : '';
    },
    getMetaData() {
      return API.Rest.get(`/quote/metadata?fids=${this.fids}`).then(response => {
        Object.entries(response.data.metadata).forEach(([fid, { type }]) => (this.formats[fid] = type));
      });
    },
    momentTZ(date) {
      return moment(toTimezoneDate(this.timezone, date));
    },
    fetch() {
      this.isLoading = true;
      this.isError = false;
      this.isNoData = false;
      this.nextSeriesUpdTime = null;
      this.timeseries = [];
      this.streamingUnsubscribe();
      this.streamingSubscribe();
    },
    getTimeSeriesDataThrottled(resolve) {
      if (new Date().getTime() - this.lastTimeSeries > 60000) {
        this.getTimeSeriesData().then(resolve);
      }
    },
    getTimeSeriesData() {
      this.lastTimeSeries = Date.now();
      return API.Rest.get(
        `/timeseries/intraday?ric=${this.ric}&fids=TIME,CLOSE&fromDate=${this.nextSeriesUpdTime}&interval=15`
      )
        .then(response => {
          const data = response.data.data;
          if (!data.length) return [];
          // next timeseries query should include last timeseries data block, as it gets updated with every price tick
          this.nextSeriesUpdTime = data.slice(-1)[0].TIME;
          return data.map(d => [d.TIME * 1000, parseFloat(d.CLOSE)]);
        })
        .catch(error => {
          console.error('Timeseries retrieval error', error);
          return [];
        });
    },
    // Streaming
    streamingConnect() {
      return new Promise((resolve, reject) => {
        if (API.Streaming.isConnected) resolve();
        else
          API.Streaming.connect({
            timeout: 10000,
            monitor: {
              interval: 10000,
              timeout: 5000
            },
            visibilityMonitorEnabled: true
          }).then(resolve, reject);
      });
    },
    streamingSubscribe() {
      return this.streamingConnect().then(() => {
        this.subscriptionId = API.Streaming.Quote.subscribe(
          {
            rics: [this.ric],
            fids: streamableFidsFilter(this.fids)
          },
          // Subscribe Callback
          (_response, error) => {
            if (error) {
              console.warn(`Streaming subscription error: ${error}`);
              this.isLoading = false;
              this.isNoData = true;
            }
          },
          // Snapshot Callback
          this.handleSnapshot,
          // Update Callback
          this.handleUpdate
        );
      });
    },
    streamingUnsubscribe() {
      return this.streamingConnect().then(() => {
        if (this.subscriptionId) {
          API.Streaming.Quote.unsubscribe(this.subscriptionId, (e, err) => {
            if (err) console.warn("Couldn't unsubscribe streaming", err);
          });
        }
      });
    },
    fetchRkdFidsData() {
      return API.Rest.get(
        `/quote/info?rics=${this.ric}&fids=${this.rkdFids},rkd.RECOM_MEAN_CURR,rkd4.TARGETPRICE,`
      ).then(res => {
        let rkdData = res.data.data[0];
        const recommendationValue = Number.parseFloat(rkdData['rkd.RECOM_MEAN_CURR']);
        let recomText = '';
        if (!isNaN(recommendationValue)) {
          if (recommendationValue < 1.5) recomText = 'strong-buy';
          else if (recommendationValue < 2.5) recomText = 'buy';
          else if (recommendationValue < 3.5) recomText = 'hold';
          else if (recommendationValue < 4.5) recomText = 'sell';
        }
        rkdData['recomText'] = recomText;
        for (const key in rkdData) {
          this.data[key] = rkdData[key];
        }
      });
    },
    getEarningsDate() {
      return API.Rest.get(
        `/company/earnings?rics=${this.ric}&fids=earn.COMBI_DATE,earn.MARKET_PHASE&fromDate=${moment().format(
          'YYYY-MM-DD'
        )}`
      ).then(res => {
        this.nextEarningsDate = res.data.data[0] ? res.data.data[0]['earn.COMBI_DATE'] : '';
        let marketPhase = res.data.data[0] ? res.data.data[0]['earn.MARKET_PHASE'] : '';
        this.data['nextMarketPhase'] = isNaN(marketPhase) ? '' : marketPhase;
      });
    },
    async handleSnapshot(response, error) {
      if (error) {
        console.warn('Subscription snapshot error', error);
        if (this.isLoading) {
          this.isError = true;
          this.isLoading = false;
        } else {
          this.hasStreamingError = true;
        }
        return;
      }
      this.hasStreamingError = false;
      this.data = response.payload[0].data;
      await this.fetchRkdFidsData();
      await this.getEarningsDate();
      this.nextSeriesUpdTime = new Date(this.data['q._HSTCLSDATE']).getTime() / 1000;
      this.getTimeSeriesData().then(timeseries => {
        this.timeseries = timeseries;
        this.$refs.chart.chart.reflow();
      });
      this.isLoading = false;
    },
    handleUpdate(response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming update error', error);
        return;
      }
      this.hasStreamingError = false;
      const data = response.payload[0].data;
      Object.keys(data).forEach(fid => (this.data[fid] = data[fid]));
      // do not retrieve timeseries if there was no price change
      if (data['q._TRDPRC_1']) {
        this.getTimeSeriesDataThrottled(data => {
          if (!data.length) return;
          let matchIndex = this.timeseries.length;
          while (matchIndex-- >= 0) {
            if (this.timeseries[matchIndex][0] < data[0][0]) break;
            if (this.timeseries[matchIndex][0] === data[0][0]) {
              // ignore overlapping cached timeseries data and just retrieved data
              while (this.timeseries[matchIndex] && this.timeseries[matchIndex].toString() === data[0].toString()) {
                data.shift();
                matchIndex++;
              }
              this.timeseries.splice(matchIndex);
              break;
            }
          }
          this.timeseries.push(...data);
        });
      }
    },
    setWidgetViewMode() {
      if (this.$el)
        this.viewMode = this.$el.getBoundingClientRect().width < this.viewBreakpoint ? 'vertical' : 'horizontal';
    },
    getChartTitle() {
      return `${this.i18n('chart.intraday')} ${this.i18n('chartGraphImage', 'globalLabels')}`;
    }
  },
  created() {
    this.getMetaData().then(this.fetch);
  },
  mounted() {
    this.setWidgetViewMode();
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
  }
};
</script>
